import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useActivity, useLocales, usePlatforms, useProjects } from '../api/hooks'
import type { ActivityEntry, Revision } from '../api/types'
import { labelMap } from '../lib/locales'
import { timeAgo } from '../lib/time'

const ACTOR_TYPES: (Revision['actor_type'] | 'all')[] = ['all', 'user', 'ai', 'api', 'import', 'revert']
const ACTOR_GLYPH: Record<Revision['actor_type'], string> = {
  user: '✎',
  ai: '◆',
  api: '⇅',
  import: '⇩',
  revert: '↺',
}
const LIMIT = 50

export function Activity() {
  const [platform, setPlatform] = useState('')
  const [locale, setLocale] = useState('')
  const [actorType, setActorType] = useState<Revision['actor_type'] | 'all'>('all')
  const [offset, setOffset] = useState(0)

  const platforms = usePlatforms()
  const locales = useLocales()
  const labels = labelMap(locales.data ?? [])
  const activity = useActivity({
    platform: platform || undefined,
    locale: locale || undefined,
    actorType: actorType === 'all' ? undefined : actorType,
    limit: LIMIT,
    offset,
  })

  const resetToFirstPage = () => setOffset(0)
  const total = activity.data?.total ?? 0
  const shown = activity.data?.entries.length ?? 0
  const pageStart = total === 0 ? 0 : offset + 1
  const pageEnd = offset + shown

  return (
    <div className="h-full overflow-y-auto bg-ink-50 px-4 py-4">
      <div className="mx-auto max-w-6xl space-y-3">
        <div>
          <h1 className="text-base font-semibold text-ink-900">Activity</h1>
          <p className="text-xs text-ink-600">
            Every translation write, across every platform and locale — the full audit trail.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2 text-xs">
          <label className="flex flex-col gap-1">
            <span className="label">Platform</span>
            <select
              className="field py-1"
              value={platform}
              onChange={(event) => {
                setPlatform(event.target.value)
                resetToFirstPage()
              }}
            >
              <option value="">All</option>
              {(platforms.data ?? []).map((p) => (
                <option key={p.id} value={p.slug}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="label">Locale</span>
            <select
              className="field py-1"
              value={locale}
              onChange={(event) => {
                setLocale(event.target.value)
                resetToFirstPage()
              }}
            >
              <option value="">All</option>
              {(locales.data ?? []).map((l) => (
                <option key={l.code} value={l.code}>
                  {labels[l.code]?.short ?? l.code}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="label">Actor type</span>
            <select
              className="field py-1"
              value={actorType}
              onChange={(event) => {
                setActorType(event.target.value as Revision['actor_type'] | 'all')
                resetToFirstPage()
              }}
            >
              {ACTOR_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All' : type}
                </option>
              ))}
            </select>
          </label>
        </div>

        {activity.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
            Could not load activity: {(activity.error as Error).message}
          </p>
        )}

        <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-ink-100 text-2xs uppercase tracking-wider text-ink-600">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Actor</th>
                <th className="px-3 py-2 text-left font-semibold">Target</th>
                <th className="px-3 py-2 text-left font-semibold">Locale</th>
                <th className="px-3 py-2 text-left font-semibold">Change</th>
                <th className="px-3 py-2 text-left font-semibold">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {activity.isLoading && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-ink-600">
                    Loading activity…
                  </td>
                </tr>
              )}
              {(activity.data?.entries ?? []).map((entry) => (
                <ActivityRow key={entry.id} entry={entry} localeLabel={labels[entry.locale]?.short ?? entry.locale} />
              ))}
              {activity.isSuccess && activity.data.entries.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-ink-600">
                    No activity matches these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-2xs text-ink-600">
          <span>
            {total > 0 ? `Showing ${pageStart}–${pageEnd} of ${total}` : activity.isLoading ? 'Loading…' : '0 results'}
          </span>
          <div className="flex gap-1.5">
            <button
              className="btn"
              disabled={offset === 0 || activity.isLoading}
              onClick={() => setOffset((prev) => Math.max(0, prev - LIMIT))}
            >
              Previous
            </button>
            <button
              className="btn"
              disabled={!activity.data?.has_more || activity.isLoading}
              onClick={() => setOffset((prev) => prev + LIMIT)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ActivityRow({ entry, localeLabel }: { entry: ActivityEntry; localeLabel: string }) {
  const projects = useProjects(entry.platform ?? undefined)
  const project = projects.data?.find((item) => item.id === entry.project_id)
  const projectSlug = project ? (project.is_default ? 'default' : (project.slug ?? 'default')) : 'default'

  return (
    <tr>
      <td className="px-3 py-2">
        <span
          className="mr-1 inline-block w-4 text-center text-ink-600"
          title={entry.actor_type}
          aria-label={entry.actor_type}
        >
          {ACTOR_GLYPH[entry.actor_type]}
        </span>
        <span title={entry.actor.email ?? undefined}>{entry.actor.display}</span>
      </td>
      <td className="px-3 py-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1 font-mono text-2xs text-ink-600">
            <span>{entry.platform ?? '—'}</span>
            <span aria-hidden="true">/</span>
            <span>{entry.project ?? '—'}</span>
          </div>
          <div className="truncate font-mono text-xs text-ink-800" title={entry.key_name}>
            {entry.key_name}
          </div>
          {entry.platform && (
            <Link
              to={`/p/${entry.platform}/${projectSlug}?key=${encodeURIComponent(entry.key_id)}&locale=${encodeURIComponent(entry.locale)}`}
              className="text-2xs text-blue-700 underline hover:no-underline"
            >
              Open in editor
            </Link>
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        <span className="chip border-ink-200 bg-ink-50 text-ink-700">{localeLabel}</span>
      </td>
      <td className="max-w-sm px-3 py-2 text-ink-700">
        {entry.old_value ? (
          <>
            <span className="line-through text-ink-500">{entry.old_value}</span>{' '}
            <span aria-hidden="true">→</span>{' '}
          </>
        ) : null}
        <span className="text-ink-900">{entry.new_value}</span>
      </td>
      <td className="px-3 py-2 text-ink-600">{timeAgo(entry.created_at)}</td>
    </tr>
  )
}
